'use client';

import { useRouter } from 'next/navigation';
import React, { useState } from 'react';
import { encodePassphrase, generateRoomId, randomString } from '@/lib/client-utils';
import styles from '../styles/Home.module.css';

function Tabs(props: React.PropsWithChildren<{}>) {
  const router = useRouter();
  const initialTab =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('tab') === 'custom'
      ? 1
      : 0;
  const [tabIndex, setTabIndex] = useState<number>(initialTab);

  function onTabSelected(index: number) {
    setTabIndex(index);
    const tab = index === 1 ? 'custom' : 'demo';
    router.push(`/?tab=${tab}`);
  }

  const tabs = React.Children.map(props.children, (child, index) => (
    <button
      className="lk-button"
      onClick={() => onTabSelected(index)}
      aria-pressed={tabIndex === index}
    >
      {/* @ts-ignore */}
      {child?.props.label}
    </button>
  ));

  return (
    <div className={styles.tabContainer}>
      <div className={styles.tabSelect}>{tabs}</div>
      {/* @ts-ignore */}
      {props.children[tabIndex]}
    </div>
  );
}

function NewSessionTab(props: { label: string }) {
  const router = useRouter();
  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));
  const [customRoomName, setCustomRoomName] = useState('');

  const startMeeting = () => {
    const slug = customRoomName.trim()
      ? customRoomName
          .toLowerCase()
          .replace(/[^a-z0-9-]+/g, '-')
          .replace(/^-+|-+$/g, '')
      : '';
    const finalRoom = slug || generateRoomId();
    const target = e2ee
      ? `/rooms/${finalRoom}#${encodePassphrase(sharedPassphrase)}`
      : `/rooms/${finalRoom}`;
    router.push(target);
  };

  return (
    <div className={styles.tabContent}>
      <p className={styles.tabHint}>
        Start a secure live video session for your patients and team.
      </p>

      <input
        type="text"
        placeholder="Room name (optional)"
        value={customRoomName}
        onChange={(ev) => setCustomRoomName(ev.target.value)}
        className={styles.formInput}
      />

      <button className={`lk-button ${styles.primaryButton}`} onClick={startMeeting}>
        Start Meeting
      </button>

      <div className={styles.optionsGroup}>
        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={e2ee}
            onChange={(ev) => setE2ee(ev.target.checked)}
          />
          <span>Enable end-to-end encryption</span>
        </label>
        {e2ee && (
          <div className={styles.optionsRow}>
            <label htmlFor="passphrase-new">Passphrase</label>
            <input
              id="passphrase-new"
              type="password"
              value={sharedPassphrase}
              onChange={(ev) => setSharedPassphrase(ev.target.value)}
              className={styles.formInput}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function JoinExistingTab(props: { label: string }) {
  const router = useRouter();
  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));
  const [serverUrl, setServerUrl] = useState('wss://live.mindlabhealth.com');
  const [token, setToken] = useState('');

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const target = e2ee
      ? `/custom/?liveKitUrl=${encodeURIComponent(serverUrl)}&token=${encodeURIComponent(token)}#${encodePassphrase(sharedPassphrase)}`
      : `/custom/?liveKitUrl=${encodeURIComponent(serverUrl)}&token=${encodeURIComponent(token)}`;
    router.push(target);
  };

  return (
    <form className={styles.tabContent} onSubmit={onSubmit}>
      <p className={styles.tabHint}>
        Connect with a custom LiveKit server URL and access token.
      </p>

      <input
        type="url"
        placeholder="LiveKit Server URL"
        required
        value={serverUrl}
        onChange={(ev) => setServerUrl(ev.target.value)}
        className={styles.formInput}
      />

      <textarea
        placeholder="Token"
        required
        rows={5}
        value={token}
        onChange={(ev) => setToken(ev.target.value)}
        className={styles.formTextarea}
      />

      <div className={styles.optionsGroup}>
        <label className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={e2ee}
            onChange={(ev) => setE2ee(ev.target.checked)}
          />
          <span>Enable end-to-end encryption</span>
        </label>
        {e2ee && (
          <div className={styles.optionsRow}>
            <label htmlFor="passphrase-join">Passphrase</label>
            <input
              id="passphrase-join"
              type="password"
              value={sharedPassphrase}
              onChange={(ev) => setSharedPassphrase(ev.target.value)}
              className={styles.formInput}
            />
          </div>
        )}
      </div>

      <hr className={styles.divider} />

      <button
        type="submit"
        className={`lk-button ${styles.primaryButton} ${styles.fullWidth}`}
      >
        Connect
      </button>
    </form>
  );
}

function BrandHeader() {
  return (
    <div className={styles.brandHeader}>
      <div className={styles.brandRow}>
        <div className={styles.brandIconWrap}>
          <img
            src="/images/mindlab-logo.svg"
            alt="MindLab Health logo"
            width={56}
            height={56}
            className={styles.brandIcon}
          />
        </div>
        <div className={styles.brandText}>
          <div className={styles.brandEyebrow}>MindLab Health</div>
          <h1 className={styles.brandTitle}>MLH Meet</h1>
          <div className={styles.brandSubtitle}>
            Secure live video sessions for patients and care teams
          </div>
        </div>
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer className={styles.footer} data-lk-theme="default">
      <div className={styles.footerInner}>
        <img
          src="/images/mindlab-logo.svg"
          alt="MindLab Health"
          width={28}
          height={28}
          className={styles.footerLogo}
        />
        <div className={styles.footerText}>
          <strong className={styles.footerBrand}>MindLab Health</strong>
          <span className={styles.footerSep}>·</span>
          <span>Live video for healthcare teams.</span>
        </div>
      </div>
    </footer>
  );
}

export default function Page() {
  return (
    <>
      <main className={styles.main} data-lk-theme="default">
        <BrandHeader />
        <Tabs>
          <NewSessionTab label="New Session" />
          <JoinExistingTab label="Join Existing" />
        </Tabs>
      </main>
      <Footer />
    </>
  );
}